import type { CSSProperties } from 'react';
import type { CommunityCanvas, CommunityTextBox, ZoneBox } from '../../types/api';
import {
  CANVAS_PX,
  DARK_INK,
  LIGHT_INK,
  luminance,
  resolveColor,
  type BrandPalette,
} from './communityZones';

/**
 * The arithmetic behind `CommunityDesignPreview`: how a map's numbers become
 * CSS. Kept out of the component module so the studio's canvas and the static
 * preview share it without either exporting non-components from a `.tsx`.
 */

/** A px size on the 1080 canvas, as a share of the stage's width. */
export function canvasUnits(px: number): string {
  return `${(px / CANVAS_PX) * 100}cqw`;
}

/** Where a box sits on the stage. */
export function boxStyle(box: ZoneBox): CSSProperties {
  return {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };
}

/** The backdrop's paint. An image backdrop without a URL is a neutral field. */
export function backdropStyle(
  canvas: CommunityCanvas,
  palette: BrandPalette,
  imageUrl: string | null,
): CSSProperties {
  switch (canvas.kind) {
    case 'color':
      return { background: resolveColor(canvas.color, palette) };
    case 'gradient':
      return {
        background: `linear-gradient(${canvas.angle}deg, ${resolveColor(
          canvas.from,
          palette,
        )}, ${resolveColor(canvas.to, palette)})`,
      };
    case 'image':
      return imageUrl === null
        ? { background: 'rgba(28, 27, 24, 0.12)' }
        : {
            backgroundImage: `url("${imageUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          };
  }
}

/**
 * How light the backdrop reads, 0..1, for the `auto` text colour. A photo is
 * unknown until the server measures it, so it reads as dark — light ink with a
 * shadow is the renderer's usual answer on a photo.
 */
export function backdropLuminance(canvas: CommunityCanvas, palette: BrandPalette): number {
  switch (canvas.kind) {
    case 'color':
      return luminance(resolveColor(canvas.color, palette));
    case 'gradient':
      return (
        (luminance(resolveColor(canvas.from, palette)) +
          luminance(resolveColor(canvas.to, palette))) /
        2
      );
    case 'image':
      return 0.2;
  }
}

/** A text box's ink as CSS, with `auto` guessed from the backdrop. */
export function textInk(color: string, palette: BrandPalette, backdropLuma: number): string {
  switch (color) {
    case 'auto':
      return backdropLuma < 0.45 ? LIGHT_INK : DARK_INK;
    case 'light':
      return LIGHT_INK;
    case 'dark':
      return DARK_INK;
    default:
      return resolveColor(color, palette);
  }
}

/** The text a box shows: typed field, else default, else (headline) the fallback. */
export function textValue(
  text: CommunityTextBox,
  fields: Record<string, string> | undefined,
  fallbackHeadline: string,
): string {
  const typed = fields?.[text.id];
  if (typed !== undefined && typed.trim().length > 0) return typed;
  if (text.default.trim().length > 0) return text.default;
  return text.role === 'headline' ? fallbackHeadline : '';
}
